import { Component } from 'react'

import difference from 'lodash/difference'
import PropTypes from 'prop-types'
import qs from 'qs'
import { List } from 'semantic-ui-react'

import { joinElementsWithAComma } from 'utils/formatters'
import { makeRequest } from 'utils/requests'
import { fromGlobalId } from 'utils/transformations/graphql'
import { splitTextByCommaAndNewLine } from 'utils/transformations/text'
import { isValidEmailAddress } from 'utils/validations'

import Checkbox from 'components/Core/Checkbox/Checkbox'
import Divider from 'components/Core/Divider/Divider'
import Modal from 'components/Core/Modal'
import Table from 'components/Core/Table/Table'
import Textarea from 'components/Core/Textarea/Textarea'

import styles from './ResendEmailsModal.component.module.css'
import messages from './ResendEmailsModal.messages'

const RECIPIENTS_MAX_LENGTH = 250

export default class ResendEmailsModal extends Component {
  state = {
    settings: {
      custom_message: false,
      custom_recipient: true,
      exclude_terms: false,
      message_body: '',
      message_subject: '',
      additional_recipients: this.props.currentUser.email,
      single_pdf: false,
      zip: false,
    },
    invalidEmailAddresses: [],
    validEmailAddresses: [],
  }

  getAllEmailAddresses = () =>
    splitTextByCommaAndNewLine(this.state.settings.additional_recipients)

  getRequestData = () => {
    const { selectedOrders } = this.props
    const { settings, validEmailAddresses } = this.state

    return {
      ...settings,
      message_body: settings.custom_message ? settings.message_body : '',
      message_subject: '',
      additional_recipients: settings.custom_recipient
        ? validEmailAddresses.join(',')
        : '',
      order_ids: selectedOrders.map((order) => {
        // TODO(santiago): stop using fromGlobalId once we refactor the order review page
        const { id: orderId } = fromGlobalId(order.id)
        return orderId
      }),
    }
  }

  getEncodedRequestData = () => qs.stringify(this.getRequestData())

  setSetting = (settingName, value) => {
    this.setState({
      settings: {
        ...this.state.settings,
        [settingName]: value,
      },
    })
  }

  toggleSetting = (e, { value }) => {
    const { settings } = this.state
    const settingIsEnabled = settings[value]

    this.setSetting(value, !settingIsEnabled)
  }

  sendEmails = () =>
    // TODO(ch15459) rebuild endpoint
    makeRequest({
      method: 'post',
      url: '/json/orders/resend_emails',
      data: this.getEncodedRequestData(),
    })

  handlePrimaryClick = () => {
    if (this.isSubmitButtonDisabled()) {
      return
    }

    const { setPageLoaderActive, handleResetOrder } = this.props
    setPageLoaderActive(true)

    this.sendEmails()
      .then(({ data }) => {
        handleResetOrder()
        this.renderToasterMessage(data.success)
        setPageLoaderActive(false)
      })
      .catch((error) => {
        console.error(error)
        setPageLoaderActive(false)
      })
  }

  validateEmailAddresses = () => {
    const emailAddresses = this.getAllEmailAddresses()
    const validEmailAddresses = emailAddresses.filter((emailAddress) =>
      isValidEmailAddress(emailAddress),
    )

    this.setState({
      validEmailAddresses,
      invalidEmailAddresses: difference(emailAddresses, validEmailAddresses),
    })

    if (!this.state.settings.additional_recipients.length) {
      const {
        currentUser: { email },
      } = this.props
      this.setState({
        settings: {
          ...this.state.settings,
          custom_recipient: false,
          additional_recipients: email,
        },
        invalidEmailAddresses: [],
        validEmailAddresses: [email],
      })
    }
  }

  isSubmitButtonDisabled = () =>
    this.state.settings.custom_recipient &&
    this.state.invalidEmailAddresses.length > 0

  renderToasterMessage = (success) => {
    const { renderErrorNotification, renderSuccessNotification } = this.props
    if (success) {
      renderSuccessNotification(messages.successfulResend)
    } else {
      renderErrorNotification(messages.failedResend)
    }
  }

  renderInvalidEmailAddresses = () => {
    const { invalidEmailAddresses } = this.state
    const formattedText = joinElementsWithAComma(invalidEmailAddresses)
    return (
      <span className={styles.errorText}>
        {messages.invalidEmails(formattedText, invalidEmailAddresses.length)}
      </span>
    )
  }

  renderLeftColumn = () => {
    const { settings, invalidEmailAddresses } = this.state
    return (
      <List>
        <List.Item key="custom_recipient">
          <Checkbox
            label={messages.copyRecipients}
            value="custom_recipient"
            checked={settings.custom_recipient}
            onClick={this.toggleSetting}
          />
          {settings.custom_recipient && (
            <List>
              <List.Item>
                <span className={styles.smallText}>{messages.addEmails}</span>

                <Textarea
                  maxLength={RECIPIENTS_MAX_LENGTH}
                  onBlur={this.validateEmailAddresses}
                  onChange={(e, { value }) => {
                    this.setSetting('additional_recipients', value)
                  }}
                  error={invalidEmailAddresses.length > 0}
                  className={styles.textarea}
                />
                {invalidEmailAddresses.length > 0 &&
                  this.renderInvalidEmailAddresses()}
                {settings.additional_recipients.length >=
                  RECIPIENTS_MAX_LENGTH && (
                  <span className={styles.errorText}>
                    {messages.maxCharError(RECIPIENTS_MAX_LENGTH)}
                  </span>
                )}
              </List.Item>
            </List>
          )}
        </List.Item>
        <List.Item key="custom_message">
          <Checkbox
            label={messages.personalizedMessage}
            value="custom_message"
            checked={settings.custom_message}
            onClick={this.toggleSetting}
          />
          {settings.custom_message && (
            <List>
              <List.Item>
                <span>Message</span>
                <Textarea
                  placeholder={messages.addMessage}
                  onChange={(e, { value }) => {
                    this.setSetting('message_body', value)
                  }}
                />
              </List.Item>
            </List>
          )}
        </List.Item>
      </List>
    )
  }

  renderRightColumn = () => {
    const emailOptions = [
      { label: messages.mergeOrders, value: 'single_pdf' },
      { label: messages.zip, value: 'zip' },
      {
        label: messages.excludeTerms,
        value: 'exclude_terms',
      },
      {
        label: messages.excludeImages,
        value: 'exclude_images',
      },
    ]

    return (
      <List>
        {emailOptions.map((exportOption) =>
          this.renderEmailOption(exportOption),
        )}
      </List>
    )
  }

  renderEmailOption = ({ value, label }) => (
    <List.Item key={value}>
      <Checkbox
        label={label}
        value={value}
        checked={this.state.settings[value]}
        onClick={this.toggleSetting}
      />
    </List.Item>
  )

  renderSelectedOrders = () =>
    this.props.selectedOrders.map((selectedOrder) => (
      <Table.Row className="notranslate" key={selectedOrder.id}>
        <Table.Cell>{selectedOrder.retailerName}</Table.Cell>
        <Table.Cell>{selectedOrder.shippingEmail}</Table.Cell>
        <Table.Cell
          link={`/orders/review/${selectedOrder.webOrderLookupId}`}
          shouldLinkOpenInNewTab
          className={styles.poNumber}
        >
          {selectedOrder.referenceNumber}
        </Table.Cell>
      </Table.Row>
    ))

  render() {
    return (
      <Modal
        title={messages.resendEmails}
        primaryActionOnClick={this.handlePrimaryClick}
        primaryActionLabel={messages.sendEmails}
        primaryDisabled={this.isSubmitButtonDisabled()}
        large
        open
        onClose={this.props.onClose}
      >
        <span>
          <b>{messages.resendTo}</b>
        </span>
        <Table fullWidth className={styles.table}>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell width="large">
                {messages.retailer}
              </Table.HeaderCell>
              <Table.HeaderCell width="large">
                {messages.buyerEmail}
              </Table.HeaderCell>
              <Table.HeaderCell width="small">
                {messages.poNumber}
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>{this.renderSelectedOrders()}</Table.Body>
        </Table>
        <Divider />
        <span>
          <b>{messages.emailSettings}</b>
        </span>
        <div className={styles.emailSettings}>
          <div>{this.renderLeftColumn()}</div>
          <div>{this.renderRightColumn()}</div>
        </div>
      </Modal>
    )
  }
}

ResendEmailsModal.propTypes = {
  currentUser: PropTypes.object.isRequired,
  selectedOrders: PropTypes.arrayOf(PropTypes.object).isRequired,
  setPageLoaderActive: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  renderSuccessNotification: PropTypes.func.isRequired,
  renderErrorNotification: PropTypes.func.isRequired,
  handleResetOrder: PropTypes.func.isRequired,
}
